import isEmpty from 'lodash/isEmpty'

import { DeliveryInfoInput, SavedAddress } from 'src/apollo/onlineOrdering'

import { DeliveryInfo, Location } from 'public/components/online_ordering/types'
import { Customer } from './CustomerContextCommon'
import { RangedCustomerAddress } from './DeliveryContext'
import { CartDeliveryInfo } from './FulfillmentContext'

export const dummyAddressValue = 'XX'

export const normalizeGoogleAddress = (
  addressFromGoogle: google.maps.places.PlaceResult
) => {
  const normalizedAddress = {} as DeliveryInfoInput

  if (addressFromGoogle.address_components) {
    // prefer a route or street_address over a premise
    const hasStreetAddress = addressFromGoogle.address_components.some(
      (comp) =>
        comp.types.includes('route') || comp.types.includes('street_address')
    )
    for (let component of addressFromGoogle.address_components) {
      for (let componentType of component.types) {
        if (normalizedAddress) {
          if (componentType === 'street_number') {
            normalizedAddress.address1 = normalizedAddress.address1
              ? component.long_name + ' ' + normalizedAddress.address1
              : component.long_name
            break
          } else if (componentType === 'premise' && !hasStreetAddress) {
            normalizedAddress.address1 = normalizedAddress.address1
              ? normalizedAddress.address1 + ' ' + component.short_name
              : component.short_name
            break
          } else if (
            componentType === 'route' ||
            componentType === 'street_address'
          ) {
            normalizedAddress.address1 = normalizedAddress.address1
              ? normalizedAddress.address1 + ' ' + component.short_name
              : component.short_name
            break
          } else if (componentType === 'subpremise') {
            normalizedAddress.address2 = `#${component.long_name}`
            break
          } else if (
            componentType === 'locality' ||
            (componentType === 'neighborhood' && !normalizedAddress.city) ||
            (componentType === 'sublocality_level_1' &&
              !normalizedAddress.city) ||
            (componentType === 'administrative_area_level_3' &&
              !normalizedAddress.city) ||
            (componentType === 'postal_town' && !normalizedAddress.city)
          ) {
            // locality should be given priority, so always overwrite other data that may have been put in the city.
            // https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-addressform
            // Brooklyn and other parts of New York City do not include the city as part of the address. Instead, they use sublocality_level_1.
            // Based on querying the Toast Local database, some Rxs dont have a locality or sublocality_level_1, but do have a city in the
            // administrative_area_level_3 field.
            normalizedAddress.city = component.long_name
            break
          } else if (componentType === 'administrative_area_level_1') {
            normalizedAddress.state = component.short_name
            // normalises administrative area to be alligned with state, this addition allows us to enable OO delivery for Canadian customers
            // this will be updated with greater support for international in the coming weeks
            normalizedAddress.administrativeArea = component.short_name
            break
          } else if (componentType === 'postal_code') {
            normalizedAddress.zipCode = component.short_name
            break
          } else if (componentType === 'country') {
            normalizedAddress.country = component.short_name
            break
          }
        }
      }
    }
  }

  if (normalizedAddress && addressFromGoogle.geometry?.location) {
    normalizedAddress.latitude = addressFromGoogle.geometry.location.lat()
    normalizedAddress.longitude = addressFromGoogle.geometry.location.lng()
  }

  if (normalizedAddress.country && normalizedAddress.country !== 'US') {
    // state is not a relevant field for international countries, so we default to the dummy state value 'XX'
    normalizedAddress.state = dummyAddressValue
    if (isEmpty(normalizedAddress.zipCode)) {
      // zipcode is not always found for UKI addresses, so we default to a dummy value 'XX'
      normalizedAddress.zipCode = dummyAddressValue
    }
  }
  return isEmpty(normalizedAddress) ? null : normalizedAddress
}

export const formatLocationAddress = (
  address: Location | null | undefined,
  includeZip: boolean = true
) => {
  if (!address) return ''

  const stateOrAdminArea = address.administrativeArea || address.state
  const stateDisplay =
    stateOrAdminArea && stateOrAdminArea !== dummyAddressValue
      ? `, ${stateOrAdminArea}`
      : ''
  const zipDisplay =
    includeZip && address.zip !== dummyAddressValue ? ` ${address.zip}` : ''
  const address2Display = address.address2 ? `, ${address.address2}` : ''

  return `${address.address1}${address2Display}, ${address.city}${stateDisplay}${zipDisplay}`
}

export const getGoogleMapsDirectionsUrl = (
  location: Location | null | undefined
): string => {
  const formattedAddress = formatLocationAddress(location)
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    formattedAddress
  )}`
}

export const formatDeliveryAddress = (
  address: DeliveryInfo,
  options: {
    full?: boolean
    includeAddress2?: boolean
    includeZip?: boolean
  } = {}
) => {
  if (!address) {
    return ''
  }
  const optionsWithDefaults = {
    includeAddress2: true,
    includeZip: true,
    ...options
  }

  const line1 = formatDeliveryAddressLine1(
    address,
    optionsWithDefaults.includeAddress2
  )
  const line2 = formatDeliveryAddressLine2(
    address,
    optionsWithDefaults.includeZip
  )

  return options.full ? `${line1}, ${line2}` : line1
}

export const formatDeliveryAddressLine1 = (
  address: DeliveryInfo,
  includeAddress2: boolean = true
) => {
  if (!address) {
    return ''
  }
  const address2 = address?.address2 ? `, ${address.address2}` : ''
  return `${address.address1}${includeAddress2 && address2 ? address2 : ''}`
}

export const formatDeliveryAddressLine2 = (
  address: DeliveryInfo,
  includeZip: boolean = true
) => {
  if (!address) {
    return ''
  }
  const zipcode =
    address.zipCode === dummyAddressValue || !includeZip
      ? ''
      : ` ${address.zipCode}`

  const state =
    address.state && address.state !== dummyAddressValue ? address.state : ''
  return `${address.city}, ${address.administrativeArea || state}${zipcode}`
}

/*
 * Display pattern for addresses
 * If the address has a name, display it on the first line and the rest of the address on the second line
 * If the address does not have a name, display addressLine1 and addressLine2 on the first line and the rest on the second line
 */
export const getAddressDisplay = (addr: SavedAddress) => {
  const addressWithoutName = `${addr.deliveryInfo.address1}${
    addr.deliveryInfo.address2 ? `, ${addr.deliveryInfo.address2}` : ''
  }`
  const displayLabel = addr.name || addressWithoutName
  const formattedAddress = addr.name
    ? `${addr.deliveryInfo.address1}${
        addr.deliveryInfo.address2 ? `, ${addr.deliveryInfo.address2}` : ''
      }, ${formatDeliveryAddressLine2(addr.deliveryInfo, true)}`
    : formatDeliveryAddressLine2(addr.deliveryInfo, true)
  return { displayLabel, formattedAddress }
}

// For US, CA, IE, and UK addresses
export const normalizeAddressString = (
  address?: DeliveryInfoInput | null,
  showAddress2 = true
) => {
  if (!address) {
    return ''
  }

  const { address1, address2, city, state, zipCode, administrativeArea } =
    address ?? {}
  // International countries return 'XX' for state
  let stateOrAdminArea =
    administrativeArea || (state !== dummyAddressValue ? state : '')
  const cleanedZip = zipCode === dummyAddressValue ? '' : zipCode
  return [
    address1,
    showAddress2 ? address2 : '',
    city,
    stateOrAdminArea,
    cleanedZip
  ]
    .filter(Boolean)
    .join(', ')
}

const postalCodePatterns: Record<string, RegExp> = {
  US: /^\d{5}(-\d{4})?$/, // 12345 or 12345-6789
  CA: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, // A1A 1A1
  UK: /^[A-Za-z]{1,2}\d{1,2}[A-Za-z]?\s*\d[A-Za-z]{2}$/, // SW1A 1AA,
  IE: /^[A-Za-z]\d{2}\s?[A-Za-z0-9]{4}$/ // A12 G1B2
}

const isPostalCode = (value: string): boolean => {
  return Object.values(postalCodePatterns).some((pattern) =>
    pattern.test(value)
  )
}

export const hasPostalCode = (
  prediction: google.maps.places.AutocompletePrediction
) => {
  if (!prediction.terms || prediction.terms.length === 0) return false

  return prediction.terms.some((term) => isPostalCode(term.value))
}

// Finds the saved address that matches the delivery info
// if no match is found, returns a new saved address with the delivery info
export const toSavedAddress = (
  deliveryInfo: DeliveryInfo | null | undefined,
  customer: Customer
): SavedAddress | null => {
  if (!deliveryInfo) return null
  const savedAddresses = customer?.addresses

  const address = savedAddresses?.find((address) =>
    isAddressMatch(address, deliveryInfo)
  )
  return (
    address ?? {
      name: null,
      deliveryInfo: deliveryInfo,
      guid: 'not-saved-address'
    }
  )
}

/**
 * Utility function to determine if a delivery address matches a saved customer address
 * Matches by GUID first, then falls back to address property field matching
 */
export const isAddressMatch = (
  customerAddress: RangedCustomerAddress | SavedAddress,
  deliveryInfo: CartDeliveryInfo
): boolean => {
  // Match by GUID if both have GUIDs (most reliable)
  if (deliveryInfo.guid && customerAddress.guid === deliveryInfo.guid) {
    return true
  }

  const isMatch = (
    saved: string | null | undefined,
    delivery: string | null | undefined
  ) => {
    // This is necessary as savedAddresses stores empty strings as null
    const savedCleaned = saved ? saved.toLowerCase() : ''
    const deliveryCleaned = delivery ? delivery.toLowerCase() : ''
    return savedCleaned === deliveryCleaned
  }

  const customerDeliveryInfo = customerAddress.deliveryInfo

  return (
    isMatch(deliveryInfo?.address1, customerDeliveryInfo.address1) &&
    isMatch(deliveryInfo?.address2, customerDeliveryInfo.address2) &&
    isMatch(deliveryInfo?.city, customerDeliveryInfo.city) &&
    isMatch(deliveryInfo?.state, customerDeliveryInfo.state) &&
    isMatch(deliveryInfo?.zipCode, customerDeliveryInfo.zipCode) &&
    isMatch(deliveryInfo?.country, customerDeliveryInfo.country)
  )
}
